import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Award, Search, Download, Filter, Calendar, School, User } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Certificate {
  id: string;
  issue_date: string;
  user_id: string;
  course_id: string;
  certificate_url: string | null;
  profiles: {
    first_name: string | null;
    last_name: string | null;
    school: string | null;
    position: string | null;
    school_district: string | null;
  };
  courses: {
    title: string;
    hours: number;
    category: string;
  };
}

const ManageCertificates = () => {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [timeRange, setTimeRange] = useState("all");
  const [sortBy, setSortBy] = useState("date");

  const { data: certificates = [], isLoading } = useQuery({
    queryKey: ['admin-certificates'],
    queryFn: async () => {
      console.log("Fetching certificates...");
      
      const { data, error } = await supabase
        .from('certificates')
        .select(`
          *,
          courses:course_id(title, hours, category)
        `)
        .order('issue_date', { ascending: false });

      if (error) {
        console.error("Error fetching certificates:", error);
        toast({
          title: "Error",
          description: "Failed to load certificates",
          variant: "destructive",
        });
        return [];
      }

      // If we got the certificates, now let's get the profiles with school_district
      if (data && data.length > 0) {
        const userIds = data.map(cert => cert.user_id);
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, school, position, school_district')
          .in('id', userIds);

        if (profilesError) {
          console.error("Error fetching profiles:", profilesError);
          return [];
        }

        // Combine the data
        const combined = data.map(cert => ({
          ...cert,
          profiles: profilesData?.find(p => p.id === cert.user_id) || {
            first_name: null,
            last_name: null,
            school: null,
            position: null,
            school_district: null
          }
        }));

        console.log("Combined data:", combined);
        return combined as unknown as Certificate[];
      }

      return [];
    },
  });

  const filteredCertificates = certificates.filter(cert => {
    const searchString = `${cert.profiles.first_name} ${cert.profiles.last_name} ${cert.courses.title}`.toLowerCase();
    const matchesSearch = searchString.includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === "all" ? true : cert.courses.category === filterCategory;
    
    const certDate = new Date(cert.issue_date);
    const now = new Date();
    let matchesTimeRange = true;

    switch (timeRange) {
      case "month":
        matchesTimeRange = certDate >= new Date(now.setMonth(now.getMonth() - 1));
        break;
      case "year":
        matchesTimeRange = certDate >= new Date(now.setFullYear(now.getFullYear() - 1));
        break;
      default:
        matchesTimeRange = true;
    }

    return matchesSearch && matchesCategory && matchesTimeRange;
  }).sort((a, b) => {
    switch (sortBy) {
      case "name":
        return `${a.profiles.last_name}${a.profiles.first_name}`.localeCompare(
          `${b.profiles.last_name}${b.profiles.first_name}`
        );
      case "course":
        return a.courses.title.localeCompare(b.courses.title);
      case "hours":
        return b.courses.hours - a.courses.hours;
      default:
        return new Date(b.issue_date).getTime() - new Date(a.issue_date).getTime();
    }
  });

  const exportCertificates = () => {
    const csvData = filteredCertificates.map(cert => ({
      "Issue Date": new Date(cert.issue_date).toLocaleDateString(),
      "First Name": cert.profiles.first_name || "",
      "Last Name": cert.profiles.last_name || "",
      "School District": cert.profiles.school_district || "",
      "School": cert.profiles.school || "",
      "Position": cert.profiles.position || "",
      "Course": cert.courses.title,
      "Category": cert.courses.category,
      "Hours": cert.courses.hours
    }));

    const csvContent = "data:text/csv;charset=utf-8," + 
      Object.keys(csvData[0]).join(",") + "\n" +
      csvData.map(row => Object.values(row).join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "certificates_export.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Export Complete",
      description: "Your certificates have been exported to CSV",
    });
  };

  return (
    <div className="w-full py-1 px-1 space-y-4 animate-fade-in">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-title font-bold text-primary flex items-center gap-3">
            <Award className="h-8 w-8" />
            Certificate Management
          </h1>
          <p className="text-muted-foreground mt-2 font-subtitle">
            View and manage all issued certificates
          </p>
        </div>
        <Button onClick={exportCertificates} className="gap-2 font-body">
          <Download className="h-4 w-4" />
          Export Data
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-section font-medium flex items-center gap-2">
              <Award className="h-4 w-4" />
              Total Certificates
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-title font-bold">{certificates.length}</div>
            <p className="text-xs text-muted-foreground font-caption">Issued to date</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-section font-medium flex items-center gap-2">
              <School className="h-4 w-4" />
              Unique Schools
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-title font-bold">
              {new Set(certificates.map(c => c.profiles.school)).size}
            </div>
            <p className="text-xs text-muted-foreground font-caption">Participating schools</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-section font-medium flex items-center gap-2">
              <User className="h-4 w-4" />
              Unique Recipients
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-title font-bold">
              {new Set(certificates.map(c => c.user_id)).size}
            </div>
            <p className="text-xs text-muted-foreground font-caption">Total recipients</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or course..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 font-section"
          />
        </div>
        
        <div className="flex gap-2 w-full md:w-auto">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[160px] font-section">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Time period" />
            </SelectTrigger>
            <SelectContent className="font-body">
              <SelectItem value="all">All time</SelectItem>
              <SelectItem value="month">Last month</SelectItem>
              <SelectItem value="year">Last year</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[160px] font-section">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent className="font-body">
              <SelectItem value="date">Latest first</SelectItem>
              <SelectItem value="name">Recipient name</SelectItem>
              <SelectItem value="course">Course title</SelectItem>
              <SelectItem value="hours">Hours (High to Low)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-section">Date Issued</TableHead>
                <TableHead className="font-section">Recipient</TableHead>
                <TableHead className="font-section">District</TableHead>
                <TableHead className="font-section">School</TableHead>
                <TableHead className="font-section">Position</TableHead>
                <TableHead className="font-section">Course</TableHead>
                <TableHead className="font-section">Category</TableHead>
                <TableHead className="font-section text-right">Hours</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 font-body">
                    Loading certificates...
                  </TableCell>
                </TableRow>
              ) : filteredCertificates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 font-body">
                    No certificates found
                  </TableCell>
                </TableRow>
              ) : (
                filteredCertificates.map((cert) => (
                  <TableRow key={cert.id}>
                    <TableCell className="font-body">
                      {new Date(cert.issue_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="font-body">
                      {cert.profiles.first_name} {cert.profiles.last_name}
                    </TableCell>
                    <TableCell className="font-body">{cert.profiles.school_district || "-"}</TableCell>
                    <TableCell className="font-body">{cert.profiles.school || "-"}</TableCell>
                    <TableCell className="font-body">{cert.profiles.position || "-"}</TableCell>
                    <TableCell className="font-body">{cert.courses.title}</TableCell>
                    <TableCell className="font-body">{cert.courses.category}</TableCell>
                    <TableCell className="text-right font-body">{cert.courses.hours}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default ManageCertificates;
